import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setField } from 'actions/matrixSettings';
import { SettingsNumberField, SettingsButtonSet } from 'components/SettingsNew';
import { useValidateMatix } from 'hooks/matrices';
import { writePermissions } from 'constants/auth';
import { getMatrix, getExperiments } from 'constants/matrices';
import MatrixSharingSettings from './MatrixSharingSettings';
import './MatrixSettings.scss';

const MatrixSettingsAdvanced = ({ matrix, isEditing }) => {
  const dispatch = useDispatch();
  const { validationMessages } = useSelector(state => state.matrixSettings);
  const { userData, user } = useSelector(state => state.auth);
  const { matrices } = useSelector(state => state.matrices);

  const validateMatrix = useValidateMatix();

  useEffect(() => {
    validateMatrix();
  }, [matrix, validateMatrix]);

  const hasWriteAccess = useMemo(() => {
    const userId = user?.claims?.user_id;
    return writePermissions.includes(matrix?.roles?.[userId]);
  }, [user, matrix]);

  const canChangeIsPublic = (matrix, matrices, userData) => {
    if (!userData.isAdmin) return false;

    // If parent matrix is not public, no changing this experiment's publicity
    if (matrix.parentId && !getMatrix(matrices, matrix.parentId)?.isPublic) {
      return false;
    }

    // If matrix has any public children, no changing this matrix's publicity
    // (would leave some public children with a private parent.
    if (getExperiments(matrices, matrix).some(m => m.isPublic)) {
      return false;
    }

    return true;
  };

  if (!matrix) return null;

  return (
    <div className="MatrixSettingsAdvanced">
      <div className="MatrixSettings-section-title">Advanced settings</div>

      <div className="MatrixSettings-row">
        <div className="MatrixSettings-column MatrixSettings-half">
          <SettingsNumberField
            label="Curve"
            onChange={value => dispatch(setField('curveSteepness', value))}
            validationMessage={validationMessages.curveSteepness}
            step="0.1"
            value={matrix.curveSteepness}
            description="Curve steepness affects the steepness of the curve of the activation functions for each node in this matrix. (Default recommendation: 1)"
            isStatic={!isEditing}
          />
        </div>
        <div className="MatrixSettings-column MatrixSettings-half">
          <SettingsNumberField
            label="Created Lambda"
            onChange={value => dispatch(setField('lambda', value))}
            validationMessage={validationMessages.lambda}
            step="0.01"
            value={matrix.lambda}
            description="Lambda is a relaxation parameter that speeds convergence of the fuzzy cognitive map. (Default recommendation: 0.5)"
            isStatic={!isEditing}
          />
        </div>
      </div>

      <div className="MatrixSettings-row MatrixSettings-half">
        <SettingsNumberField
          label="Tolerance"
          onChange={value => dispatch(setField('tolerance', value))}
          validationMessage={validationMessages.tolerance}
          step="0.001"
          value={matrix.tolerance}
          description="Tolerance is the value used to determine whether a node’s relative abundance has changed from one iteration to the next. Smaller values will make the model run for more iterations as more nodes will appear to have changed in an iteration. (Default recommendation: 0.001)"
          isStatic={!isEditing}
        />
      </div>

      <div className="MatrixSettings-section-title">Edit access</div>

      <div className="MatrixSettings-row">
        <div className="MatrixSettings-column MatrixSettings-half MatrixSettings-align-buttonset">
          <SettingsButtonSet
            disabled={!hasWriteAccess}
            label="Type"
            onChange={value => {
              dispatch(setField('isViewOnly', value === 'viewOnly'));
            }}
            options={[
              { value: 'editable', label: 'Editable' },
              { value: 'viewOnly', label: 'View-only' },
            ]}
            value={matrix?.isViewOnly ? 'viewOnly' : 'editable'}
            description={
              'View-only files cannot be edited although users may still duplicate the file.'
            }
            isStatic={!isEditing}
          />
        </div>
        {canChangeIsPublic(matrix, matrices, userData) ? (
          <div className="MatrixSettings-column MatrixSettings-half">
            <SettingsButtonSet
              disabled={!canChangeIsPublic(matrix, matrices, userData)}
              label="Location"
              onChange={value => {
                dispatch(setField('isPublic', value === 'yes'));
              }}
              options={[
                { value: 'yes', label: 'Public' },
                { value: 'no', label: 'Private' },
              ]}
              value={matrix.isPublic ? 'yes' : 'no'}
              description={
                'Public matrices are available in the public gallery to all users.'
              }
              isStatic={!isEditing}
            />
          </div>
        ) : null}
      </div>

      <MatrixSharingSettings matrix={matrix} isStatic={!isEditing} />
    </div>
  );
};

export default MatrixSettingsAdvanced;
